<script setup lang="ts">
import type { AppCamperSearch } from '@yescapa-dev/ysc-api-js/modern'

interface Props {
  camper: AppCamperSearch
  /** The value that will be used for the `date_from` query parameter for the link to the camper page */
  dateFrom?: string
  /** The value that will be used for the `date_to` query parameter for the link to the camper page */
  dateTo?: string
}

const props = withDefaults(defineProps<Props>(), {
  dateFrom: undefined,
  dateTo: undefined,
})

defineEmits<{
  wishlist: [AppCamperSearch]
}>()

const { n } = useI18n()

const { isCamperInWishlist: isInWishlist } = storeToRefs(useWishlistsStore())

const vehicleType = computed(() => useVehicleType({ type: props.camper.vehicle_type.toLowerCase() }))
const reviewAverage = computed(() => n(props.camper.display_review_average, { minimumFractionDigits: 1, maximumFractionDigits: 1, style: 'decimal' }))
const passengers = computed(() => Math.min(props.camper.vehicle_seatbelts, props.camper.vehicle_beds))
const camperInWishlist = computed(() => isInWishlist.value(props.camper.id))

const { bookingPricePerDay, bookingTotalPrice, startingPrice } = useSearchCamperDisplayPrice(() => props.camper)
</script>

<template>
  <div class="flex w-full max-w-sm flex-row overflow-hidden rounded border bg-white shadow md:w-1/2">
    <div class="relative w-1/4">
      <NuxtLink
        :to="buildCampersIdRoute({ camper, dateFrom: props.dateFrom, dateTo: props.dateTo })"
        target="_blank"
      >
        <span
          class="absolute inset-0"
          aria-hidden="true"
        />
        <div
          v-if="camper.picture_set[0].type === 'twicpics'"
          :data-twic-background="`url('image:${twicpicsPath(camper.picture_set[0].path)}')`"
          class="h-full w-full bg-cover bg-center"
        />
        <div
          v-else
          :style="`background-image : url(${camper.picture_set[0].path})`"
          class="h-full w-full bg-cover bg-center"
        />
      </NuxtLink>
    </div>
    <div class="relative flex w-3/4 flex-col p-3">
      <NuxtLink
        :to="buildCampersIdRoute({ camper, dateFrom: props.dateFrom, dateTo: props.dateTo })"
        target="_blank"
      >
        <span
          class="absolute inset-0"
          aria-hidden="true"
        />
        <div class="flex items-center justify-between">
          <span class="font-semibold">{{ vehicleType }}</span>
          <button
            v-if="camper.source === 'yescapa'"
            class="z-50"
            @click.prevent="$emit('wishlist', camper)"
          >
            <YscIconsHeartSolid
              v-if="camperInWishlist"
              class="h-5 w-5 text-pink-500"
            />
            <YscIconsHeart
              v-else
              class="h-5 w-5 text-current"
            />
          </button>
        </div>
      </NuxtLink>
      <div class="mt-0.5 flex items-end justify-between text-sm">
        <div class="flex flex-col space-y-1">
          <div class="leading-tight">
            {{ $t('components.app_product_card.vehicles.passengers_plural', { count_number: $n(passengers) }, passengers) }}
          </div>
          <div
            class="flex items-baseline space-x-1"
          >
            <YscIconsStarSolid
              class="w-[0.875rem] h-[0.875rem] place-self-center"
              :class="camper.review_count > 0 ? 'text-pink-500' : 'text-gray-500'"
            />
            <template v-if="camper.review_count > 0">
              <span class="font-semibold">{{ reviewAverage }}</span>
              <span
                v-if="camper.is_ambassador"
                class="text-xs font-semibold text-yellow-700"
              >{{ $t('commons.best_owner') }}</span>
            </template>
            <span
              v-else
              class="text-sm leading-snug text-gray-500"
            >
              {{ camper.is_new ? $t('commons.new') : $t('commons.no_reviews') }}
            </span>
          </div>
        </div>
        <div class="flex-end flex flex-col items-end">
          <YscCamperCardPriceDisplay
            :booking-daily-price="bookingPricePerDay"
            :booking-total-price="bookingTotalPrice"
            :starting-price="startingPrice"
          />
        </div>
      </div>
    </div>
  </div>
</template>
